/**
 * usePlaybackControl Hook
 * 
 * 管理影片播放控制功能，包括：
 * - 快轉/倒轉
 * - 逐幀前進/後退
 * - 播放速度控制
 * - 長按加速
 * 
 * @author AI Assistant
 * @date 2025-11-02
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// 步進單位類型
export type StepUnit = 'second' | 'frame';

// Hook 配置
export interface PlaybackControlConfig {
  defaultFastForwardSeconds?: number;
  defaultStepUnit?: StepUnit;
}

// Hook 返回類型
export interface UsePlaybackControlReturn {
  // 狀態
  fastForwardSeconds: number;
  stepUnit: StepUnit;
  isLongPressingFastForward: boolean;
  isLongPressingRewind: boolean;
  isLongPressingStepBackward: boolean;
  isLongPressingStepForward: boolean;
  originalPlaybackRate: number;

  // 操作函數
  setFastForwardSeconds: (seconds: number) => void;
  setStepUnit: (unit: StepUnit) => void;
  setIsLongPressingFastForward: (pressing: boolean) => void;
  setIsLongPressingRewind: (pressing: boolean) => void;
  setIsLongPressingStepBackward: (pressing: boolean) => void;
  setIsLongPressingStepForward: (pressing: boolean) => void;
  setOriginalPlaybackRate: (rate: number) => void;

  // Refs
  fastForwardLongPressTimerRef: React.RefObject<NodeJS.Timeout | null>;
  rewindLongPressTimerRef: React.RefObject<NodeJS.Timeout | null>;
  stepBackwardLongPressTimerRef: React.RefObject<NodeJS.Timeout | null>;
  stepForwardLongPressTimerRef: React.RefObject<NodeJS.Timeout | null>;

  // 輔助函數
  resetLongPressStates: () => void;
  clearAllTimers: () => void;
}

/**
 * 播放控制 Hook
 * 
 * @param config 配置選項
 * @returns {UsePlaybackControlReturn} 播放控制狀態和操作函數
 * 
 * @example
 * ```typescript
 * const {
 *   fastForwardSeconds,
 *   setFastForwardSeconds,
 *   isLongPressingFastForward,
 *   setIsLongPressingFastForward
 * } = usePlaybackControl({
 *   defaultFastForwardSeconds: 5,
 *   defaultStepUnit: 'second'
 * });
 * ```
 */
export const usePlaybackControl = (
  config: PlaybackControlConfig = {}
): UsePlaybackControlReturn => {
  const {
    defaultFastForwardSeconds = 5,
    defaultStepUnit = 'second',
  } = config;

  // ========== 播放控制狀態 ==========
  const [fastForwardSeconds, setFastForwardSecondsState] = useState<number>(() => {
    const saved = localStorage.getItem('fastForwardSeconds');
    return saved ? Number(saved) : defaultFastForwardSeconds;
  });

  const [stepUnit, setStepUnitState] = useState<StepUnit>(() => {
    const saved = localStorage.getItem('stepUnit');
    return (saved as StepUnit) || defaultStepUnit;
  });

  // ========== 長按狀態 ==========
  const [isLongPressingFastForward, setIsLongPressingFastForward] = useState(false);
  const [isLongPressingRewind, setIsLongPressingRewind] = useState(false);
  const [isLongPressingStepBackward, setIsLongPressingStepBackward] = useState(false);
  const [isLongPressingStepForward, setIsLongPressingStepForward] = useState(false);
  const [originalPlaybackRate, setOriginalPlaybackRate] = useState(1);

  // ========== 長按計時器 Refs ==========
  const fastForwardLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const rewindLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stepBackwardLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stepForwardLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ========== 帶 localStorage 同步的 setter ==========
  const setFastForwardSeconds = useCallback((seconds: number) => {
    setFastForwardSecondsState(seconds);
    localStorage.setItem('fastForwardSeconds', seconds.toString());
  }, []);

  const setStepUnit = useCallback((unit: StepUnit) => {
    setStepUnitState(unit);
    localStorage.setItem('stepUnit', unit);
  }, []);

  // ========== 輔助函數 ==========
  
  /**
   * 重置所有長按狀態
   */
  const resetLongPressStates = useCallback(() => {
    setIsLongPressingFastForward(false);
    setIsLongPressingRewind(false);
    setIsLongPressingStepBackward(false);
    setIsLongPressingStepForward(false);
  }, []);

  /**
   * 清除所有長按計時器
   */
  const clearAllTimers = useCallback(() => {
    if (fastForwardLongPressTimerRef.current) {
      clearTimeout(fastForwardLongPressTimerRef.current);
      fastForwardLongPressTimerRef.current = null;
    }
    if (rewindLongPressTimerRef.current) {
      clearTimeout(rewindLongPressTimerRef.current);
      rewindLongPressTimerRef.current = null;
    }
    if (stepBackwardLongPressTimerRef.current) {
      clearTimeout(stepBackwardLongPressTimerRef.current);
      stepBackwardLongPressTimerRef.current = null;
    }
    if (stepForwardLongPressTimerRef.current) {
      clearTimeout(stepForwardLongPressTimerRef.current);
      stepForwardLongPressTimerRef.current = null;
    }
  }, []);

  // ========== 清理 ==========
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // ========== 返回 ==========
  return {
    // 狀態
    fastForwardSeconds,
    stepUnit,
    isLongPressingFastForward,
    isLongPressingRewind,
    isLongPressingStepBackward,
    isLongPressingStepForward,
    originalPlaybackRate,

    // 操作函數
    setFastForwardSeconds,
    setStepUnit,
    setIsLongPressingFastForward,
    setIsLongPressingRewind,
    setIsLongPressingStepBackward,
    setIsLongPressingStepForward,
    setOriginalPlaybackRate,

    // Refs
    fastForwardLongPressTimerRef,
    rewindLongPressTimerRef,
    stepBackwardLongPressTimerRef,
    stepForwardLongPressTimerRef,

    // 輔助函數
    resetLongPressStates,
    clearAllTimers,
  };
};

export default usePlaybackControl;

